import './LinkModal.scss'
import { useEffect, useState } from 'react'
import Modal from '../../components/Modal'
import { listTricounts, sendToTricount } from '../../ApiInterface'
import { canSendToTricount, equalShares, formatCurrency } from '../../Helpers'

interface Props {
    bankTransaction: BankTransaction
    // Possible doublon dans un compte Tricount : proposer de le lier plutôt
    duplicateId?: number | null
    onLinkInstead: (id: number) => void
    onCancel: () => void
    onDone: () => void
}

// Envoie une opération de banque dans un Tricount : dépense payée par moi, répartie à parts égales.
const TricountEntryModal = ({ bankTransaction, duplicateId, onLinkInstead, onCancel, onDone }: Props) => {
    const [tricounts, setTricounts] = useState(null as Tricount[] | null)
    const [tricountId, setTricountId] = useState(null as number | null)
    const [label, setLabel] = useState(bankTransaction.user_label || bankTransaction.label)
    const [memberUuids, setMemberUuids] = useState([] as string[])
    const [error, setError] = useState('')
    const [sending, setSending] = useState(false)

    const alreadyIn = (tricount: Tricount) => bankTransaction.statement_links.some((link) => link.account_id === tricount.account_id)

    useEffect(() => {
        listTricounts().then((all) => {
            const eligible = all.filter((tricount) => canSendToTricount(tricount) && !alreadyIn(tricount))
            setTricounts(eligible)
            if(eligible.length === 1) {
                setTricountId(eligible[0].id)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const tricount = tricounts?.find((t) => t.id === tricountId) || null

    // Tous les membres cochés par défaut.
    useEffect(() => {
        setMemberUuids(tricount ? tricount.members.map((member) => member.uuid) : [])
    }, [tricount])

    const checkedMembers = tricount ? tricount.members.filter((member) => memberUuids.includes(member.uuid)) : []
    const shares = equalShares(bankTransaction.amount_cents, Math.max(checkedMembers.length, 1))
    const shareOf = (uuid: string) => {
        const index = checkedMembers.findIndex((member) => member.uuid === uuid)
        return index === -1 ? null : shares[index]
    }
    const myShare = tricount?.membership_uuid ? shareOf(tricount.membership_uuid) : null

    const toggle = (uuid: string) => setMemberUuids((uuids) => uuids.includes(uuid) ? uuids.filter((u) => u !== uuid) : [...uuids, uuid])

    const confirm = () => {
        if(!tricount || checkedMembers.length === 0 || sending) {
            return
        }
        setSending(true)
        setError('')
        sendToTricount(bankTransaction.id, { tricount_id: tricount.id, label, member_uuids: checkedMembers.map((member) => member.uuid) })
            .then(onDone)
            .catch((e) => {
                setSending(false)
                setError(e.response?.data?.message || "L'envoi dans Tricount a échoué")
            })
    }

    return <Modal onCancel={onCancel}>
        <div className="link-modal">
            <h3>Envoyer dans un Tricount</h3>
            <div className="sub">La dépense est créée dans Tricount, payée par vous, puis liée à cette opération</div>
            <div className="from">
                <div className="t"><b>{ bankTransaction.user_label || bankTransaction.label }</b></div>
                <div className="m">{ formatCurrency(bankTransaction.amount_cents) }</div>
            </div>
            { tricounts && tricounts.length === 0 && <div className="preview warning">
                Aucun Tricount disponible : il doit être activé, en EUR, et ne pas déjà contenir cette opération.
            </div> }
            { duplicateId && <div className="preview warning">
                Une opération du même montant existe peut-être déjà dans un Tricount.{' '}
                <a href="#link" onClick={(e) => { e.preventDefault(); onLinkInstead(duplicateId) }}>La lier plutôt</a>
            </div> }
            { tricounts && tricounts.length > 0 && <>
                <div className="search">
                    <select className="tl-select" value={tricountId ?? ''} onChange={(e) => setTricountId(e.target.value ? Number(e.target.value) : null)}>
                        <option value="">Choisir un Tricount</option>
                        { tricounts.map((t) => <option key={t.id} value={t.id}>{ t.title }</option>) }
                    </select>
                    <input className="tl-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Libellé"/>
                </div>
                { tricount && <div className="candidates">
                    <h4>Répartition égale</h4>
                    { tricount.members.map((member) => {
                        const share = shareOf(member.uuid)
                        return <label key={member.uuid} className="candidate">
                            <input type="checkbox" checked={memberUuids.includes(member.uuid)} onChange={() => toggle(member.uuid)}/>
                            <div className="t"><b>{ member.name }{ member.uuid === tricount.membership_uuid && ' (moi)' }</b></div>
                            <div className="m">{ share === null ? '–' : formatCurrency(share) }</div>
                        </label>
                    }) }
                </div> }
                { tricount && myShare !== null && <div className="preview">
                    Ma part : <b>{ formatCurrency(myShare) }</b> sur { formatCurrency(bankTransaction.amount_cents) }. Elle sera comptée dans les budgets à la place du total.
                </div> }
            </> }
            { error && <div className="preview warning">{ error }</div> }
            <div className="buttons">
                <button className="tl-btn tl-btn-ghost" onClick={onCancel}>Annuler</button>
                <button className="tl-btn tl-btn-primary" disabled={!tricount || checkedMembers.length === 0 || sending} onClick={confirm}>
                    { sending ? 'Envoi…' : 'Envoyer dans Tricount' }
                </button>
            </div>
        </div>
    </Modal>
}

export default TricountEntryModal
